import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import {
  useAddAccountMutation,
  useUpdateAccountMutation,
} from "src/api/tree/tree";

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  balance: yup.number().min(0, "Must be 0 or greater").required("Required"),
  showInPLReport: yup.boolean(),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IModel {
  open: boolean;
  handleClose: () => void;
  dataToEdit?: any;
  refetch: Function;
  parent?: any;
}

const AddEditRecord = (props: IModel) => {
  const { open, handleClose, dataToEdit, refetch, parent } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !dataToEdit ? "add" : "edit";
  const { control, handleSubmit, formState, reset, setValue } =
    useForm<FormInputs>({
      mode: "onBlur",
      resolver: yupResolver(schema),
      defaultValues: {},
    });
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  // APIS
  const {
    mutate: mutateAddAccount,
    isPending: isPendingAddAccount,
    status: statusAddAccount,
    error: errorAddAccount,
  } = useAddAccountMutation();
  const {
    mutate: mutateUpdateAccount,
    isPending: isPendingUpdateAccount,
    status: statusUpdateAccount,
    error: errorUpdateAccount,
  } = useUpdateAccountMutation();

  const canShowInPLReport =
    dataToEdit?.show_at_profit_and_loss_report !== undefined;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (mode === "add") {
      const accountDataToAdd = {
        ...(parent.node_type === "category" ||
        parent.node_type === "sub_category"
          ? { chart_account_category_id: parent?.id }
          : { parent_account_id: parent?.id }),
        name: data.name,
        start_balance: data.balance,
        show_at_client_invoices: false,
      };
      mutateAddAccount(accountDataToAdd);
    }

    if (mode === "edit") {
      mutateUpdateAccount({
        id: dataToEdit.id,
        name: data.name,
        show_at_client_invoices: false,
        start_balance: data.balance,
        ...(canShowInPLReport
          ? { show_at_profit_and_loss_report: data.showInPLReport }
          : {}),
      });
    }
  };

  useEffect(() => {
    if (dataToEdit) {
      reset({
        name: dataToEdit.name,
        balance: dataToEdit.start_balance,
        showInPLReport: !!dataToEdit.show_at_profit_and_loss_report,
      });
    }
  }, [dataToEdit]);

  //Add user success and error handling
  useEffect(() => {
    if (statusAddAccount === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (statusAddAccount === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorAddAccount?.data.errors?.[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusAddAccount]);

  //Update user success and error handling
  useEffect(() => {
    if (statusUpdateAccount === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdateAccount === "error") {
      const apiError = setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdateAccount?.data.errors?.[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdateAccount]);

  console.log({ dataToEdit, parent });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? generalT("add") : generalT("edit")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("name")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                />
              )}
            />
            {dataToEdit?.source_type !== "capital" ? (
              <Controller
                name="balance"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    label={generalT("startBalance")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!formState.errors.balance}
                    helperText={formState.errors.balance?.message}
                  />
                )}
              />
            ) : null}
            {canShowInPLReport && (
              <Controller
                name="showInPLReport"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ mt: 1.5, width: "fit-content" }}
                    control={<Checkbox {...field} color="primary" />}
                    label={t("showInProfitAndLossReport")}
                    checked={field.value}
                  />
                )}
              />
            )}
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              loading={isPendingAddAccount || isPendingUpdateAccount}
              variant="contained"
              fullWidth
            >
              {mode === "add" ? generalT("add") : generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditRecord;
